import type { CareOrder, IndicatorBadgeProps } from '@talos/kyoko';
import {
  Box,
  Divider,
  HStack,
  IndicatorBadge,
  IndicatorBadgeVariants,
  InlineFormattedNumber,
  Meter,
  SideEnum,
  Text,
  toBigWithDefault,
  VStack,
} from '@talos/kyoko';
import Big from 'big.js';
import { useMemo, type ReactNode } from 'react';

interface CareOrderNettingHeaderProps {
  workingOrder?: CareOrder;
  memberCareOrders: CareOrder[];
  showAllDecimals: boolean;
}

export const CareOrderNettingHeader = ({
  workingOrder,
  memberCareOrders,
  showAllDecimals,
}: CareOrderNettingHeaderProps) => {
  // Need to differentiate these two: the Buy/Sell totals are computed from `memberCareOrders` and should use their currency (often Units),
  // even if the preview working order is converted into currency space (e.g. BTC) via the selected SizeMode.
  const workingOrderCurrency = workingOrder?.Currency ?? 'Units';
  const memberCareOrderCurrency = memberCareOrders.at(0)?.Currency ?? 'Units';

  const { buyQty, sellQty } = useMemo(
    () =>
      memberCareOrders.reduce(
        (totals, careOrder) => {
          const orderQty = toBigWithDefault(careOrder.OrderQty, 0);

          if (careOrder.Side === SideEnum.Buy) {
            totals.buyQty = totals.buyQty.plus(orderQty);
          }

          if (careOrder.Side === SideEnum.Sell) {
            totals.sellQty = totals.sellQty.plus(orderQty);
          }

          return totals;
        },
        { buyQty: Big(0), sellQty: Big(0) }
      ),
    [memberCareOrders]
  );
  const buyQtyAbsolute = buyQty.abs();
  const sellQtyAbsolute = sellQty.abs();
  const absoluteTotalQty = buyQtyAbsolute.plus(sellQtyAbsolute);
  const buyQtyPercentage = absoluteTotalQty.gt(0) ? buyQtyAbsolute.div(absoluteTotalQty) : Big(0);
  const sellQtyPercentage = absoluteTotalQty.gt(0) ? sellQtyAbsolute.div(absoluteTotalQty) : Big(0);
  const orderQty = toBigWithDefault(workingOrder?.OrderQty, 0);
  const netQtyFromPreview = workingOrder?.Side === SideEnum.Sell ? orderQty.times(-1) : orderQty;
  const isNetSell = netQtyFromPreview.lt(0);

  return (
    <HStack gap="spacing12" justifyContent="space-between" alignItems="center" p="spacing16">
      <HeaderItem
        label="Asset"
        testid="care-order-netting-header-asset"
        content={<Text color="colorTextImportant">{workingOrder?.Symbol ?? '-'}</Text>}
      />

      <HStack gap="spacing12" justifyContent="flex-end" ml="auto">
        <HeaderItem
          label="Buy"
          variant={IndicatorBadgeVariants.Positive}
          testid="care-order-netting-header-buy"
          content={
            <InlineFormattedNumber number={buyQty} currency={memberCareOrderCurrency} truncate={!showAllDecimals} />
          }
        />
        <Divider orientation="vertical" />
        <HeaderItem
          label="Sell"
          variant={IndicatorBadgeVariants.Negative}
          testid="care-order-netting-header-sell"
          content={
            <InlineFormattedNumber number={sellQty} currency={memberCareOrderCurrency} truncate={!showAllDecimals} />
          }
        />
        <Divider orientation="vertical" />
        <HeaderItem
          label="Repartition"
          testid="care-order-netting-header-repartition"
          content={
            <HStack gap="spacing8">
              <Text color="colors.green.lighten">{`${buyQtyPercentage.times(100).toFixed(2)}%`}</Text>
              <Box w="100px">
                <Meter
                  value={buyQtyPercentage.toNumber()}
                  showLabel={false}
                  showInitialAnimation
                  animate
                  color="colors.green.lighten"
                  backgroundColor="colors.red.lighten"
                />
              </Box>
              <Text color="colors.red.lighten">{`${sellQtyPercentage.times(100).toFixed(2)}%`}</Text>
            </HStack>
          }
        />
        <Divider orientation="vertical" />
        <HeaderItem
          label="Net Quantity"
          testid="care-order-netting-header-net-amount"
          content={
            <HStack gap="spacing6">
              <Text color={isNetSell ? 'colors.red.lighten' : 'colors.green.lighten'}>
                {isNetSell ? 'Sell' : 'Buy'}
              </Text>
              <InlineFormattedNumber
                number={netQtyFromPreview.toFixed()}
                currency={workingOrderCurrency}
                truncate={!showAllDecimals}
                align="right"
              />
            </HStack>
          }
        />
      </HStack>
    </HStack>
  );
};

interface HeaderItemProps {
  label: string;
  content: ReactNode;
  variant?: IndicatorBadgeProps['variant'];
  testid?: string;
}

function HeaderItem({ label, content, variant, testid }: HeaderItemProps) {
  return (
    <VStack gap="spacing4" alignItems="flex-start" data-testid={testid}>
      <IndicatorBadge children={label} variant={variant} />
      {content}
    </VStack>
  );
}
